import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Paper,
  Typography,
  Box,
  List,
  ListItemButton,
  Chip,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import { useQuery } from "react-query";
import { fetchData } from "../../../../api/client";
import { useProductSearch } from "../../../../hooks/useProducts";
import { useTranslation } from "react-i18next";

const DEBOUNCE_MS = 350;

function parseRelatedSkus(relatedSkus) {
  return String(relatedSkus || "")
    .split(/[,;\n\r]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function RelatedProductsSection({
  formData,
  handleText,
  currentProductId,
}) {
  const { t } = useTranslation("products");

  const [relatedSearchInput, setRelatedSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(relatedSearchInput.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [relatedSearchInput]);

  const idsProjetosProduto =
    formData.ids_projetos != null && formData.ids_projetos !== ""
      ? String(formData.ids_projetos)
      : null;

  const { data: searchData, isLoading: relatedLoading } = useProductSearch(
    debouncedSearchTerm,
    50,
    idsProjetosProduto
  );
  const searchProducts = searchData?.data ?? searchData?.products ?? [];

  const selectedSkus = useMemo(
    () => parseRelatedSkus(formData.related_skus),
    [formData.related_skus]
  );
  const selectedSkusSet = useMemo(
    () => new Set(selectedSkus.map((s) => s.toLowerCase())),
    [selectedSkus]
  );

  const { data: projectProductsResponse } = useQuery(
    ["products", "byProject", idsProjetosProduto],
    () =>
      fetchData(
        `/products?ids_projetos=${encodeURIComponent(idsProjetosProduto)}&limit=200&page=1`
      ),
    {
      enabled: selectedSkus.length > 0 && !!idsProjetosProduto,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const [initialRelatedLabels, setInitialRelatedLabels] = useState({});
  const projectProductsList = useMemo(
    () =>
      Array.isArray(projectProductsResponse?.data)
        ? projectProductsResponse.data
        : [],
    [projectProductsResponse]
  );

  useEffect(() => {
    if (selectedSkus.length === 0 || projectProductsList.length === 0) return;
    const set = new Set(selectedSkus.map((s) => s.toLowerCase()));
    const map = {};
    projectProductsList.forEach((p) => {
      const sku = String(p.sku || "").trim();
      if (!sku) return;
      const key = sku.toLowerCase();
      if (set.has(key)) {
        map[key] = { sku, nome: p?.nome || sku };
      }
    });
    setInitialRelatedLabels((prev) => (Object.keys(map).length ? { ...prev, ...map } : prev));
  }, [projectProductsList, selectedSkus]);

  const relatedSearchResults = useMemo(() => {
    if (!idsProjetosProduto || String(debouncedSearchTerm || "").trim().length < 1) {
      return [];
    }
    const list = Array.isArray(searchProducts) ? searchProducts : [];
    return list.filter(
      (p) =>
        String(p.id) !== String(currentProductId) &&
        !selectedSkusSet.has(String(p.sku || "").toLowerCase())
    );
  }, [searchProducts, currentProductId, idsProjetosProduto, debouncedSearchTerm, selectedSkusSet]);

  const showRelatedResults =
    idsProjetosProduto && String(relatedSearchInput || "").trim().length >= 1;

  const relatedLabelsBySkuRef = useRef(new Map());

  useEffect(() => {
    relatedSearchResults.forEach((p) => {
      const sku = String(p.sku || "").trim();
      if (sku) {
        relatedLabelsBySkuRef.current.set(sku.toLowerCase(), {
          sku,
          nome: p?.nome || sku,
        });
      }
    });
  }, [relatedSearchResults]);

  const addRelatedProduct = (product) => {
    const sku = String(product?.sku || "").trim();
    if (!sku) return;
    if (selectedSkusSet.has(sku.toLowerCase())) {
      setRelatedSearchInput("");
      return;
    }
    relatedLabelsBySkuRef.current.set(sku.toLowerCase(), {
      sku,
      nome: product?.nome || sku,
    });
    const next = [...selectedSkus, sku];
    handleText({
      target: { name: "related_skus", value: next.join(", ") },
    });
    setRelatedSearchInput("");
  };

  const removeRelatedProduct = (skuToRemove) => {
    const next = selectedSkus.filter(
      (s) => String(s).toLowerCase() !== String(skuToRemove).toLowerCase()
    );
    handleText({
      target: { name: "related_skus", value: next.join(", ") },
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={t("form.relatedProducts")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 0,
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 1,
                transition: "border-color 0.2s, box-shadow 0.2s",
                "&:focus-within": {
                  borderColor: "primary.main",
                  boxShadow: "0 0 0 1px var(--mui-palette-primary-main)",
                },
              }}
            >
              <Typography
                component="label"
                variant="body2"
                sx={{
                  display: "block",
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                {t("form.relatedProducts")}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={t("form.relatedPlaceholder")}
                value={relatedSearchInput}
                onChange={(e) => setRelatedSearchInput(e.target.value)}
                disabled={!idsProjetosProduto}
                autoComplete="off"
                variant="standard"
                sx={{
                  px: 2,
                  pb: 1,
                  "& .MuiInput-root": { "&::before, &::after": { display: "none" } },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: relatedLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  px: 2,
                  pb: 1.5,
                  color: "text.secondary",
                  lineHeight: 1.4,
                }}
              >
                {idsProjetosProduto
                  ? t("form.relatedShowcaseHint")
                  : t("form.relatedSelectCompanyHint")}
              </Typography>
              {showRelatedResults && (
                <Paper
                  variant="outlined"
                  square
                  sx={{
                    mx: 2,
                    mb: 1.5,
                    maxHeight: 280,
                    overflow: "auto",
                    borderColor: "divider",
                    bgcolor: "action.hover",
                  }}
                >
                  {relatedLoading && relatedSearchResults.length === 0 ? (
                    <Box sx={{ py: 2, textAlign: "center" }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t("form.searching")}
                      </Typography>
                    </Box>
                  ) : relatedSearchResults.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      {t("form.noProductsFound")}
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {relatedSearchResults.map((product) => (
                        <ListItemButton
                          key={product.id}
                          onClick={() => addRelatedProduct(product)}
                          sx={{ py: 1 }}
                        >
                          {product.sku ? (
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                fontWeight={600}
                                sx={{ mr: 1 }}
                              >
                                {product.sku}
                              </Typography>
                              <Typography component="span" variant="body2" color="text.secondary">
                                {product.nome}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2">{product.nome}</Typography>
                          )}
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Paper>
              )}
              {selectedSkus.length > 0 && (
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "action.hover" : "grey.50",
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedSkus.map((sku) => {
                      const label =
                        initialRelatedLabels[sku.toLowerCase()] ||
                        relatedLabelsBySkuRef.current.get(sku.toLowerCase());
                      const displayLabel = label
                        ? `[${label.sku}] ${label.nome}`
                        : sku;
                      return (
                        <Chip
                          key={sku}
                          label={displayLabel}
                          size="medium"
                          onDelete={() => removeRelatedProduct(sku)}
                          deleteIcon={<CancelIcon fontSize="small" />}
                          variant="filled"
                          color="default"
                          sx={{
                            fontWeight: 500,
                            "& .MuiChip-deleteIcon": { fontSize: 18 },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

